import React, { useEffect, useState } from "react";
import AuthWrapper from "../components/AuthWrapper/AuthWrapper";
import classes from "../Styles/RiderApplication.module.css";
import {
  FaCloudUploadAlt,
  FaCheckCircle,
  FaRegClock,
  FaTimesCircle,
  FaFileAlt,
  FaPowerOff,
} from "react-icons/fa";
import { BsTrash } from "react-icons/bs";
import PagesTitleCol from "../components/cols/PagesTitleCol";
import {
  useGetRequestQuery,
  useUploadFileMutation,
} from "../store/services/users";
import Swal from "sweetalert2";
import { RiderImageURLDocumentPath } from "../ImageUrl/ImageUrl";
import { Link, useNavigate } from "react-router-dom";

/* ---------------------------------------------------------- */
/* Status badge                                                */
/* ---------------------------------------------------------- */
function StatusBadge({ status }: { status?: string }) {
  if (!status) {
    return (
      <span className={`${classes.badge} ${classes.pending}`}>
        <FaTimesCircle size={11} /> Missing
      </span>
    );
  }
  if (status === "approved") {
    return (
      <span className={`${classes.badge} ${classes.approved}`}>
        <FaCheckCircle size={11} /> Approved
      </span>
    );
  }
  if (status === "in review") {
    return (
      <span className={`${classes.badge} ${classes.inreview}`}>
        <FaRegClock size={11} /> In review
      </span>
    );
  }
  return (
    <span className={`${classes.badge} ${classes.rejected}`}>
      <FaTimesCircle size={11} /> {status}
    </span>
  );
}

/* ---------------------------------------------------------- */
/* Reusable drag-and-drop document upload card                 */
/* ---------------------------------------------------------- */
interface DocumentUploadFieldProps {
  fieldName: string;
  title: string;
  description: string;
  status?: string;
  fileUrl?: string;
  onFileSelected: (file: File, fieldName: string) => void;
  onDelete: (fieldName: string, fileUrl: string) => void;
}

const ACCEPTED_TYPES = ["image/jpeg", "image/jpg", "image/png"];
const ACCEPTED_EXTENSIONS = [".jpg", ".jpeg", ".png"];

function isAcceptedFile(file: File) {
  const nameLower = file.name.toLowerCase();
  const hasAcceptedExt = ACCEPTED_EXTENSIONS.some((ext) =>
    nameLower.endsWith(ext)
  );
  // Check both MIME type and extension — some OSes/browsers report an
  // empty or generic type (e.g. for files dragged from certain sources),
  // so relying on type alone isn't enough.
  return ACCEPTED_TYPES.includes(file.type) || hasAcceptedExt;
}

function DocumentUploadField({
  fieldName,
  title,
  description,
  status,
  fileUrl,
  onFileSelected,
  onDelete,
}: DocumentUploadFieldProps) {
  const [isDragging, setIsDragging] = useState(false);
  const inputId = `upload-${fieldName}`;

  const rejectFile = () => {
    Swal.fire(
      "Unsupported file type",
      "Only JPG and PNG images are accepted. Please choose a different file.",
      "error"
    );
  };

  const handleDragOver = (e: React.DragEvent<HTMLLabelElement>) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent<HTMLLabelElement>) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const handleDrop = (e: React.DragEvent<HTMLLabelElement>) => {
    e.preventDefault();
    setIsDragging(false);
    const file = e.dataTransfer.files?.[0];
    if (!file) return;
    if (!isAcceptedFile(file)) {
      rejectFile();
      return;
    }
    onFileSelected(file, fieldName);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    // allow re-selecting the same file later
    e.target.value = "";
    if (!file) return;
    if (!isAcceptedFile(file)) {
      rejectFile();
      return;
    }
    onFileSelected(file, fieldName);
  };

  const isInReview = status === "in review" || status === "approved";

  return (
    <div className={classes.docCard}>
      <div className={classes.docCardHead}>
        <div>
          <h2>{title}</h2>
          <p>{description}</p>
        </div>
        <StatusBadge status={status} />
      </div>

      {isInReview && fileUrl ? (
        <div className={classes.docPreviewLink}>
          <div className={classes.docPreviewIcon}>
            <FaFileAlt size={15} />
          </div>
          <div className={classes.docPreviewText}>
            <a
              target="_blank"
              rel="noreferrer"
              href={RiderImageURLDocumentPath() + fileUrl}
            >
              View uploaded document
            </a>
            <span>Tap to open in a new tab</span>
          </div>
          {status === "in review" && (
            <button
              type="button"
              className={classes.deleteBtn}
              onClick={() => onDelete(fieldName, fileUrl)}
              aria-label={`Remove ${title}`}
            >
              <BsTrash size={13} />
            </button>
          )}
        </div>
      ) : (
        <label
          htmlFor={inputId}
          className={`${classes.dropzone} ${isDragging ? classes.dragging : ""}`}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
        >
          <input
            type="file"
            accept=".jpg,.jpeg,.png,image/jpeg,image/png"
            name={fieldName}
            id={inputId}
            onChange={handleInputChange}
          />
          <div className={classes.dropzoneIcon}>
            <FaCloudUploadAlt size={18} />
          </div>
          <span className={classes.dropzoneTitle}>
            {isDragging ? "Drop to upload" : "Drag & drop or click to upload"}
          </span>
          <span className={classes.dropzoneHint}>
            <b>Browse</b> · JPG or PNG only
          </span>
        </label>
      )}
    </div>
  );
}

/* ---------------------------------------------------------- */
/* Main page                                                    */
/* ---------------------------------------------------------- */
export default function RiderApplication() {
  const [PostRequest, { isLoading }] = useUploadFileMutation();
  const [image, setImage] = useState<string>("");
  const [showPreview, setShowPreview] = useState<boolean>(false);
  const [enteredLocation, setEnteredLocation] = useState<string>("");
  const [imageBlob, setImageBlob] = useState<any>();
  const [documentName, setDocumentName] = useState<string>("");
  const navigate = useNavigate();

  const { data: rider } = useGetRequestQuery("/rider");

  const onCreateHandler = () => {
    PostRequest({
      url: "/rider",
    })
      .unwrap()
      .then((data) => {
        Swal.fire(
          "Rider Account Created!",
          "Congratulations! Your rider account has been successfully created. You may now proceed to upload the requested document for your final approval",
          "success"
        );
      })
      .catch((error) => {
        console.log(error.data.message);
        Swal.fire(error.data.message);
      });
  };

  window.scrollTo({
    top: 0,
    behavior: "smooth",
  });

  const onSelectHandler = (el: React.ChangeEvent<HTMLSelectElement>) => {
    if (el.target.value === "") return;
    PostRequest({
      url: "/update-transport-type",
      body: {
        transportType: el.target.value,
      },
    })
      .unwrap()
      .then((data) => {
        Swal.fire("Transport type updated successfully!");
        setShowPreview(false);
      });
  };

  const handleChangeText = (el: React.ChangeEvent<HTMLInputElement>) => {
    setEnteredLocation(el.target.value);
  };

  useEffect(() => {
    let intervalId = setTimeout(() => {
      if (enteredLocation === rider?.dirvingLocation) return;

      if (enteredLocation.length < 1) return;
      PostRequest({
        url: "/update-drivng-location",
        body: {
          dirvingLocation: enteredLocation,
        },
      })
        .unwrap()
        .then((data) => {
          Swal.fire("Driving location updated!");
          setShowPreview(false);
        })
        .catch((error) => console.log(error));
    }, 1000);

    return () => clearTimeout(intervalId);
  }, [enteredLocation]);

  useEffect(() => {
    setEnteredLocation(rider?.dirvingLocation);
  }, [rider?.dirvingLocation]);

  // Shared by both the classic file input and drag-and-drop
  const handleFileSelected = (file: File, fieldName: string) => {
    const img__ = URL.createObjectURL(file);

    setImage(img__);
    setDocumentName(fieldName);
    setImageBlob(file);

    setShowPreview(true);
  };

  const onUploadHandler = () => {
    const formData = new FormData();
    formData.append("upload", imageBlob);
    formData.append("documentName", documentName);
    PostRequest({
      url: "/document",
      body: formData,
    })
      .unwrap()
      .then((data) => {
        Swal.fire(
          "Document Upload!",
          "Your document has been uploaded and is now ready for review. Please be patient while it is being evaluated.",
          "success"
        );
        setShowPreview(false);
      })
      .catch((error) => console.log(error));
  };

  const onDeleteHandler = (_documentName: string, _image: string) => {
    let confirm_ = window.confirm(
      "Are you sure you want to delete this document from the review list?"
    );
    if (!confirm_) return;
    PostRequest({
      url: "/rider-delete-document",
      body: {
        documentName: _documentName,
        image: _image,
      },
    })
      .unwrap()
      .then((data) => {
        Swal.fire("Document Deleted!");
        setShowPreview(false);
      })
      .catch((error) => console.log(error));
  };

  const onSelectColorHandler = (el: React.ChangeEvent<HTMLSelectElement>) => {
    if (el.target.value === "") return;
    PostRequest({
      url: "/update-vehicle-color",
      body: {
        carColor: el.target.value,
      },
    })
      .unwrap()
      .then((data) => {
        Swal.fire("Vehicle color updated successfully!");
        setShowPreview(false);
      });
  };

  const onLogoutHandler = () => {
    localStorage.removeItem("token");
    navigate("/rider");
  };

  // Ordered list of the document requirements, driven from data
  const documentFields: {
    fieldName: string;
    title: string;
    description: string;
  }[] = [
    {
      fieldName: "driverProfilePhoto",
      title: "Profile Picture",
      description: "A recent, clear photo of you.",
    },
    {
      fieldName: "driverLicense",
      title: "Driver's License",
      description: "Your valid driving license.",
    },
    {
      fieldName: "carOwnershipCertificate",
      title: "Proof of Car Ownership",
      description: "Certificate confirming you own the vehicle.",
    },
    {
      fieldName: "governmentID",
      title: "Valid Government ID",
      description: "A clear copy of a government-issued ID.",
    },
    {
      fieldName: "roadworthinessCertificate",
      title: "Certificate of Roadworthiness",
      description: "Confirms your vehicle meets safety standards.",
    },
    {
      fieldName: "hackneyPermit",
      title: "Hackney Permit",
      description: "Required to legally transport passengers for hire.",
    },
  ];

  return (
    <AuthWrapper>
      <PagesTitleCol
        title="On Boarding"
        description="Provide the documents below to start driving with us"
        bg_imageName="rider"
      />
      <div className={classes.page}>
        <div className={classes.main}>
          {!rider ? (
            <div className={classes.account_}>
              <h1>Quick Account Setup with Existing Credentials</h1>
              <p>
                Creating an account is simple since you already have our
                customer application. Just tap the "Create" button below to
                set up your account using the credentials you initially used
                for the customer section of Sprinterwave.
              </p>
              {isLoading ? (
                <button disabled>Please wait...</button>
              ) : (
                <button onClick={onCreateHandler}>Create account</button>
              )}
            </div>
          ) : (
            <>
              <div className={classes.form_wrapper}>
                <div className={classes.account_}>
                  <div className={classes.statusRow}>
                    <span
                      className={`${classes.statusPill} ${
                        rider?.isVerified === 1
                          ? classes.verified
                          : classes.pending
                      }`}
                    >
                      {rider?.isVerified === 1 ? (
                        <FaCheckCircle size={12} />
                      ) : (
                        <FaRegClock size={12} />
                      )}
                      {rider?.isVerified === 1 ? "Verified" : "Pending review"}
                    </span>

                    <button
                      onClick={onLogoutHandler}
                      className={classes.logoutBtn}
                    >
                      <FaPowerOff size={12} /> Logout
                    </button>
                  </div>

                  <h1>Welcome {rider?.name}!</h1>
                  <p>
                    Your account has been created! Please provide the
                    documents and information listed below to start driving
                    with us. Once your account is approved for riding, simply
                    open your Sprinterwave application and click the "Rider"
                    button that will appear, allowing you to access the rider
                    features.
                  </p>

                  {rider?.application_error_note && (
                    <div className={classes.error_reasons}>
                      <p>{rider?.application_error_note}</p>
                    </div>
                  )}
                </div>

                <div>
                  <div className={classes.sectionLabel}>
                    <span>Details</span>
                  </div>
                  <div className={classes.input_row}>
                    <div className={classes.input_wrapper}>
                      <div>
                        <h2>Desired Location *</h2>
                        <p>
                          Enter your current city or area so we can assign
                          rides near you more efficiently.
                        </p>
                      </div>
                      <div className={classes.iput_container}>
                        <input
                          type="text"
                          name="dirvingLocation"
                          value={enteredLocation}
                          id="dirvingLocation"
                          placeholder="Enter driving location"
                          onChange={handleChangeText}
                        />
                        {!rider?.dirvingLocation && (
                          <p className={classes.error}>Required</p>
                        )}
                      </div>
                    </div>

                    <div className={classes.input_wrapper}>
                      <div>
                        <h2>Transport Type *</h2>
                        <p>Select the vehicle you'll be driving with us.</p>
                      </div>
                      <div className={classes.iput_container}>
                        <select
                          id="transport"
                          name="transport"
                          defaultValue={rider?.transportType || ""}
                          onChange={onSelectHandler}
                        >
                          <option value="">Select vehicle</option>
                          <option value="vehicle">Vehicle</option>
                          <option value="motorcycle">Motorcycle</option>
                          <option value="bicycle">Bicycle</option>
                        </select>
                        {!rider?.transportType && (
                          <p className={classes.error}>Required</p>
                        )}
                      </div>
                    </div>

                    <div className={classes.input_wrapper}>
                      <div>
                        <h2>Vehicle Color *</h2>
                        <p>Pick the color of your vehicle.</p>
                      </div>
                      <div className={classes.iput_container}>
                        <select
                          name="carColor"
                          id="carColor"
                          defaultValue={rider?.carColor || ""}
                          onChange={onSelectColorHandler}
                        >
                          <option value="">Select color</option>
                          <option value="White">White</option>
                          <option value="Black">Black</option>
                          <option value="Silver">Silver</option>
                          <option value="Gray">Gray</option>
                          <option value="Blue">Blue</option>
                          <option value="Red">Red</option>
                          <option value="Green">Green</option>
                          <option value="Brown">Brown</option>
                          <option value="Beige">Beige</option>
                          <option value="Gold">Gold</option>
                        </select>
                        {!rider?.carColor && (
                          <p className={classes.error}>Required</p>
                        )}
                      </div>
                    </div>
                  </div>
                </div>

                <div>
                  <div className={classes.sectionLabel}>
                    <span>Documents</span>
                  </div>
                  <div className={classes.docGrid}>
                    {documentFields.map((field) => (
                      <DocumentUploadField
                        key={field.fieldName}
                        fieldName={field.fieldName}
                        title={field.title}
                        description={field.description}
                        status={rider?.[`${field.fieldName}Status`]}
                        fileUrl={rider?.[field.fieldName]}
                        onFileSelected={handleFileSelected}
                        onDelete={onDeleteHandler}
                      />
                    ))}
                  </div>
                </div>
              </div>

              {showPreview && (
                <div className={classes.preview_wrapper}>
                  <div className={classes.prv_section}>
                    <div>
                      <div className={classes.head}>
                        <h1>Preview</h1>
                        <FaTimesCircle onClick={() => setShowPreview(false)} />
                      </div>
                      <div>
                        <img src={image} alt="Document preview" />
                      </div>
                    </div>
                    <div>
                      {isLoading ? (
                        <button disabled>Please wait...</button>
                      ) : (
                        <button onClick={onUploadHandler}>
                          Upload document
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              )}
            </>
          )}

          {rider?.isVerified === 1 && (
            <div className={classes.verified_container}>
              <div className={classes.verified_wrapper}>
                <h1>Welcome Aboard – Your Rider Account is Verified!</h1>

                <br />

                <p>
                  Hi there,
                  <br />
                  <br />
                  We're pleased to inform you that your rider account has been
                  successfully verified! You are now ready to hit the road
                  with the SprinterWave app.
                  <br />
                  <br />
                  To get started, simply log in to the SprinterWave app on
                  your mobile device. Once logged in, you can immediately
                  begin accepting ride requests and explore various features
                  designed to make your driving experience as smooth and
                  enjoyable as possible.
                  <br />
                  <br />
                  If you have any questions or need assistance, feel free to
                  reach out to our support team. We're here to help!
                  <br />
                  <br />
                  Happy riding and welcome to the SprinterWave community!
                  <br />
                  <br />
                  Best regards,
                  <br />
                  The SprinterWave Team.
                </p>

                <div className={classes.logout_container}>
                  <Link to={"/rider"}>
                    <button>
                      <FaPowerOff /> <span>Log out</span>
                    </button>
                  </Link>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </AuthWrapper>
  );
}